import React, {useCallback, useEffect, useState} from 'react';
import {Chart as ChartJS, registerables} from 'chart.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {makeStyles, useTheme} from '@mui/styles';
import {Card, CardContent, CardHeader, Divider, IconButton, Typography} from '@mui/material';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';
import {dashboardService} from '../../../../store/services';
import {useSelector} from 'react-redux';
import LoadingSpinner from "../../../../components/Spinner/loading.spinner";
import {history} from "../../../../helpers/history";
import {InteractiveBarChart} from "../../../../components";
import {SECURITY_ROLES} from "../../../../constants/SECURITY_ROLES";

ChartJS.register(...registerables);

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%'
  },
  chartContainer: {
    position: 'relative',
    height: '300px'
  },
  stats: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center'
  },
  document: {
    textAlign: 'center',
    padding: theme.spacing(1)
  },
  documentIcon: {
    color: theme.palette.icon
  },
  titleHeader: {
    backgroundColor: theme.palette.secondary.dark,
    color: theme.palette.white
  },
}));

const ESignDocumentMetrics = props => {
  const {className, metrics: {signedDocumentData}, view, ...rest} = props;
  const user = useSelector(state => state.authentication.user);
  const [documents, setDocuments] = useState(signedDocumentData);
  const [documentCounts, setDocumentCounts] = useState(null);
  const [maxDocument, setMaxDocument] = useState(0);
  const [data, setData] = useState(null);
  const [dataIsBuilding, setDataIsBuilding] = useState(null);
  const [dataBuilt, setDataBuilt] = useState(false);
  const [labels] = useState(['Unsent', 'Pending', 'Signed']);
  const classes = useStyles();
  const theme = useTheme();
  const isUserLikeRole = user.role === SECURITY_ROLES.USER || user.role === SECURITY_ROLES.POWER_USER;


  const options = {
    plugins: {
      legend: {
        display: false
      },
    },
    scales: {
      y: {
        min: 0,
        max: maxDocument > 5 ? maxDocument : 5,
        ticks: {
          beginAtZero: true,
          count: maxDocument > 5 ? null : 5,
          stepSize: maxDocument > 5 ? null : 1
        },
      },
    },
    tooltip: {
      enabled: true,
      mode: 'point',
      intersect: false,
      usePointStyle: true,
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.primary.dark,
      titleFontColor: theme.palette.white,
    },
    minBarLength: 3,
    responsive: true,
    maintainAspectRatio: false,
  };

  const buildData = useCallback((documents) => {
    setDataIsBuilding(true);
    let documentTotals = documents.signed_totals;
    let datasets = [
      buildDatasetEntry(
        null,
        documentTotals,
        [theme.palette.privateSection, theme.palette.companySection, theme.palette.publicSection],
        theme.palette.white,
        theme.palette.white,
        8)
    ] ;

    let data = {
      datasets,
      labels
    };
    let documentCounts = [
      {
        title: 'Unsent',
        value: documentTotals['unsent_documents'],
        icon: <CancelScheduleSendIcon style={{color: theme.palette.privateSection}}/>,
        color: theme.palette.privateSection
      },
      {
        title: 'Pending',
        value: documentTotals['pending_documents'],
        icon: <PendingIcon style={{color: theme.palette.companySection}}/>,
        color: theme.palette.companySection
      },
      {
        title: 'Signed',
        value: documentTotals['completed_documents'],
        icon: <AppRegistrationIcon style={{color: theme.palette.publicSection}}/>,
        color: theme.palette.publicSection
      },
      {
        title: 'Total',
        value: documentTotals['total_documents'],
        icon: <SummarizeIcon style={{color: theme.palette.green}}/>,
        color: theme.palette.green
      }];
    let maxDocuments = Math.max.apply(Math, Object.values(documentTotals));

    setMaxDocument(maxDocuments);
    setDocumentCounts(documentCounts);
    setDataIsBuilding(false);
    setData(data);
    setDataBuilt(true);
  }, [theme, setDataIsBuilding, setDataBuilt, setDocumentCounts, setData, labels])

  function buildDatasetEntry(label, data, backgroundColor, borderColor, hoverBorderColor, borderWidth) {
    return {
      label,
      data: [data['unsent_documents'], data['pending_documents'], data['completed_documents']],
      backgroundColor,
      borderColor,
      borderWidth,
      hoverBorderColor
    }
  }

  async function resetData() {
    setData(null);
    setDataBuilt(false);
  }

  const onHandleBarClickEvent = (x) => {
    let pathname = '/esigndocuments';

    history.push({
      pathname: pathname,
      view: view,
      initialStatus: labels[x],
    });
  }

  const handleRefresh = event => {
    getData().then(metrics => {
      setDocuments(metrics);
      resetData().then(r => {
        buildData(documents)
      })
    });
  };

  async function getData() {
    return await dashboardService.getSignedDocumentMetrics(user.id).then(r => {
      return r.data
    });
  }

  useEffect(() => {
    if (!dataBuilt && !dataIsBuilding) {
      buildData(documents)
    }
  }, [dataBuilt, dataIsBuilding, buildData, documents])

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <CardHeader className={classes.titleHeader}
        action={
          <IconButton
            disabled={!dataBuilt}
            onClick={handleRefresh}
            size="small"
          >
            <RefreshIcon/>
          </IconButton>
        }
        title="Signed Document Metrics"
      />
      <Divider/>
      <CardContent>
        {data && documentCounts && !dataIsBuilding ? (
          <React.Fragment>
            <div className={classes.chartContainer}>
              <InteractiveBarChart
                data={data}
                options={options}
                onHandleBarClick={onHandleBarClickEvent}
              />
            </div>
            <div className={classes.stats}>
              {documentCounts.map(document => (
                <div
                  className={classes.document}
                  key={document.title}
                >
                  <span className={classes.documentIcon}>{document.icon}</span>
                  <Typography
                    style={{color: document.color}}
                    variant="h2"
                  >
                    {document.value}
                  </Typography>

                  <Typography
                    style={{color: document.color}}
                    variant="h6"
                  >{document.title}</Typography>

                </div>
              ))}
            </div>
          </React.Fragment>
        ) : (<LoadingSpinner/>)}
      < /CardContent>
    </Card>
  );
};

ESignDocumentMetrics.propTypes = {
  className: PropTypes.string
};

export default ESignDocumentMetrics;
